import { createSelector } from 'reselect';
import { REDACTION_STATUS_TYPE_DOCUMENT } from '@common-modules/mediaDetails/redactionStatuses';
import {
  getSelectableRedactionStatuses,
  RedactionStatusesState,
} from './models';
import { RedactionStatusesStore, defaultState, namespace } from './store';

export const selectStore = (state: object): RedactionStatusesState =>
  (state as Partial<RedactionStatusesStore>)[namespace] || defaultState;

export const selectRedactionStatusSettings = createSelector(
  selectStore,
  (s) => s.redactionStatuses
);

export const selectRedactionStatusSdoIdsByStatus = createSelector(
  selectStore,
  (s): Record<string, string> => s.sdoIdsByStatus
);

export const selectIsFetchingRedactionStatuses = createSelector(
  selectStore,
  (s) => s.isFetchingStatuses
);

/**
 * True once a status fetch has succeeded this session. Consumers must gate on
 * this before trusting `customStatuses` — an empty list otherwise reads the
 * same whether the org has no custom statuses or the fetch simply failed.
 */
export const selectHasLoadedRedactionStatuses = createSelector(
  selectStore,
  (s) => s.hasLoadedStatuses
);

export const selectHasPersistedRedactionStatusRecords = createSelector(
  selectStore,
  (s) => s.hasPersistedStatusRecords
);

export const selectIsSavingRedactionStatuses = createSelector(
  selectStore,
  (s) => s.isSavingStatuses
);

export const selectRedactionCustomStatuses = createSelector(
  selectStore,
  (s) => s.customStatuses
);

export const selectEnabledRedactionCustomStatuses = createSelector(
  selectRedactionCustomStatuses,
  // Soft-deleted rows are never offered as options; use the unfiltered
  // selector above when resolving what an existing file's tag means.
  (customStatuses) =>
    customStatuses.filter((status) => status.enabled && !status.isDeleted)
);

/**
 * The statuses a DOCUMENT may be given, in the org's configured order.
 *
 * ONE DERIVATION, because everything that reads a document's statuses has to
 * agree on it exactly. The merged-document summary buckets its files against
 * this list and builds its action menu from it, then compares the two by key
 * — and while those two steps lived on opposite sides of a prop, a parent
 * that scoped to documents and a child that did not made the target bucket
 * look like a changing one, so "all files → Complete" rewrote files already
 * at Complete. Both steps now happen in the component, and this selector is
 * why they cannot disagree about the vocabulary.
 *
 * Enabled customs only: this answers what may be OFFERED. Resolving what an
 * existing tag MEANS is a different question with a different list — see
 * `selectRedactionCustomStatuses` and `summarizeRedactionStatuses`.
 */
export const selectDocumentStatusOptions = createSelector(
  selectRedactionStatusSettings,
  selectEnabledRedactionCustomStatuses,
  (settings, customStatuses) =>
    getSelectableRedactionStatuses({
      settings,
      customStatuses,
      mediaType: REDACTION_STATUS_TYPE_DOCUMENT,
    })
);

export const selectIsStatusDrawerOpen = createSelector(
  selectStore,
  (s) => s.isStatusDrawerOpen
);

export const selectSelectedCustomStatus = createSelector(
  selectStore,
  (s) => s.selectedCustomStatus
);
